/**
 * Stan badań lekarskich i szkoleń BHP pracownika (fala 8, grupy 26, 27 i 28;
 * ADR-0058).
 *
 * Do tej pory stan liczyły dwie lokalne kopie `if (!validUntil) return 'err'`,
 * niezależnie od rodzaju i statusu rekordu, a „ostatni" rekord wybierał
 * `orderBy(desc(examDate))` — Postgres stawia NULL-e na początku, więc świeże
 * skierowanie bez daty zasłaniało ważne orzeczenie, a instruktaż stanowiskowy
 * (jednorazowy, bez daty ważności) świecił się na czerwono. Tu jest jedna
 * reguła na poziomie rekordu i jeden agregat per osoba, niezależny od
 * kolejności wierszy. Dzień `today` przychodzi argumentem (doba w Polsce,
 * ADR-0055 §7).
 *
 * Stany:
 *  - `ok` / `warn` / `err` — jak `validityState` dokumentu z terminem,
 *  - `todo` — w toku: skierowanie przed terminem albo osoba w onboardingu,
 *  - `na` — nie dotyczy: umowa cywilna bez skierowania (art. 229 i art. 237³
 *    k.p. dotyczą pracownika),
 *  - `missing` — brak jakiegokolwiek wpisu u zatrudnionego (np. osoba
 *    z importu członków): system nie wie, a nie wie, że jest źle.
 *
 * Wygasłe orzeczenie przy skierowaniu w toku to `err` — art. 229 § 4 k.p. nie
 * pozwala dopuścić do pracy bez aktualnego orzeczenia, a skierowanie tego nie
 * zmienia (decyzja właściciela 2026-10-01, D110).
 */
import { daysBetween, shiftDay, validityState, type IsoDay } from './deadlines'
import type { BhpTrainingKind } from './enums'

export const HEALTH_STATES = ['ok', 'warn', 'err', 'todo', 'na', 'missing'] as const
export type HealthState = (typeof HEALTH_STATES)[number]

/** Szkolenie wstępne — instruktaż ogólny i stanowiskowy — jest jednorazowe. */
export const ONE_TIME_BHP_KINDS: readonly BhpTrainingKind[] = ['wstepne', 'stanowiskowe']

/** Linie szkoleń BHP: każda ma własny stan, a gorsza rządzi całością. */
export type BhpLine = 'jednorazowe' | 'okresowe' | 'ppoz'

export function bhpLineOf(kind: BhpTrainingKind): BhpLine {
  if (kind === 'okresowe') return 'okresowe'
  if (kind === 'ppoz') return 'ppoz'
  return 'jednorazowe'
}

/** Termin skierowania: dzień wystawienia (w Polsce) plus liczba dni. */
export function referralDueOn(issuedOn: IsoDay | null, deadlineDays: number | null): IsoDay | null {
  if (issuedOn === null || deadlineDays === null) return null
  return shiftDay(issuedOn, deadlineDays)
}

/** Skierowanie w toku: przed terminem — w toku, po nim albo bez terminu — brak. */
function pendingState(dueOn: IsoDay | null, today: IsoDay): HealthState {
  if (dueOn === null) return 'err'
  return daysBetween(today, dueOn) >= 0 ? 'todo' : 'err'
}

export interface MedicalRecord {
  readonly id: string
  readonly status: 'referred' | 'in_progress' | 'confirmed' | 'approved' | 'cancelled'
  readonly examDate: IsoDay | null
  readonly validUntil: IsoDay | null
  /** Dzień wystawienia skierowania w Polsce; `null` = brak. */
  readonly referralIssuedOn: IsoDay | null
  readonly referralDeadlineDays: number | null
}

export interface BhpRecord {
  readonly id: string
  readonly kind: BhpTrainingKind
  readonly status: 'planned' | 'completed' | 'cancelled'
  readonly performedAt: IsoDay | null
  readonly validUntil: IsoDay | null
  readonly referralIssuedOn: IsoDay | null
  readonly referralDeadlineDays: number | null
}

function isPendingMedical(record: MedicalRecord): boolean {
  return record.status === 'referred' || record.status === 'in_progress' || record.status === 'confirmed'
}

/** Stan jednego rekordu badań. Skierowanie anulowane nie ma stanu (`null`). */
export function medicalRecordState(record: MedicalRecord, today: IsoDay): HealthState | null {
  if (record.status === 'cancelled') return null
  if (isPendingMedical(record)) {
    return pendingState(referralDueOn(record.referralIssuedOn, record.referralDeadlineDays), today)
  }
  return validityState(record.validUntil, today)
}

/**
 * Stan jednego szkolenia. Ukończone szkolenie jednorazowe bez daty ważności
 * jest zaliczone (art. 237³ § 2 k.p.), ppoż. bez daty też — przepis nie daje
 * mu okresu. Szkolenie okresowe bez daty jest niekompletne, więc `err`.
 */
export function bhpRecordState(record: BhpRecord, today: IsoDay): HealthState | null {
  if (record.status === 'cancelled') return null
  if (record.status === 'planned') {
    return pendingState(referralDueOn(record.referralIssuedOn, record.referralDeadlineDays), today)
  }
  if (record.validUntil === null || record.validUntil === '') {
    return record.kind === 'okresowe' ? 'err' : 'ok'
  }
  return validityState(record.validUntil, today)
}

/** Kolejność od najgorszego — `missing` i `na` nie biorą udziału w porównaniu linii. */
const SEVERITY: Record<HealthState, number> = { err: 5, missing: 4, warn: 3, todo: 2, ok: 1, na: 0 }

export interface AreaSummary<R> {
  readonly state: HealthState
  /** Rekord, który rozstrzyga stan (orzeczenie albo szkolenie); `null` = brak. */
  readonly governing: R | null
  /** Skierowanie w toku z terminem; `null` = brak. */
  readonly pending: { readonly record: R; readonly dueOn: IsoDay | null } | null
}

/** Kontekst osoby, gdy nie ma żadnego rekordu — rozstrzyga brak stanu. */
export interface HealthPersonContext {
  /** Osoba w procesie zatrudnienia (onboarding). */
  readonly onboarding: boolean
  /** Czy to stosunek pracy (art. 22 § 1 k.p.). */
  readonly laborCode: boolean
}

function emptyState(context: HealthPersonContext): HealthState {
  if (context.onboarding) return 'todo'
  return context.laborCode ? 'missing' : 'na'
}

interface Scored<R> {
  readonly record: R
  readonly state: HealthState
  readonly validUntil: IsoDay | null
  readonly issuedOn: IsoDay | null
  readonly dueOn: IsoDay | null
  readonly pending: boolean
}

/** Najlepszy rekord rozstrzygający: ważny przed wygasłym, potem najpóźniejsza ważność. */
function pickGoverning<R>(rows: readonly Scored<R>[]): Scored<R> | null {
  const final = rows.filter((row) => !row.pending)
  if (final.length === 0) return null
  return final.toSorted((left, right) => {
    const bySeverity = SEVERITY[left.state] - SEVERITY[right.state]
    if (bySeverity !== 0) return bySeverity
    /* Brak daty u ważnego (szkolenie jednorazowe) znaczy „bezterminowo". */
    const leftUntil = left.validUntil ?? '9999-12-31'
    const rightUntil = right.validUntil ?? '9999-12-31'
    return leftUntil < rightUntil ? 1 : leftUntil > rightUntil ? -1 : 0
  })[0]!
}

/** Najnowsze skierowanie w toku. */
function pickPending<R>(rows: readonly Scored<R>[]): Scored<R> | null {
  const pending = rows.filter((row) => row.pending)
  if (pending.length === 0) return null
  return pending.toSorted((left, right) => {
    const leftOn = left.issuedOn ?? ''
    const rightOn = right.issuedOn ?? ''
    return leftOn < rightOn ? 1 : leftOn > rightOn ? -1 : 0
  })[0]!
}

function summarizeLine<R>(rows: readonly Scored<R>[]): AreaSummary<R> | null {
  if (rows.length === 0) return null
  const governing = pickGoverning(rows)
  const pending = pickPending(rows)
  /* Ważne orzeczenie rządzi; wygasłe zostaje `err` mimo skierowania (art. 229 § 4). */
  const state = governing !== null ? governing.state : pending!.state
  return {
    state,
    governing: governing?.record ?? null,
    pending: pending === null ? null : { record: pending.record, dueOn: pending.dueOn },
  }
}

/** Badania lekarskie osoby — wynik nie zależy od kolejności wierszy. */
export function summarizeMedical(
  records: readonly MedicalRecord[],
  today: IsoDay,
  context: HealthPersonContext,
): AreaSummary<MedicalRecord> {
  const scored: Scored<MedicalRecord>[] = []
  for (const record of records) {
    const state = medicalRecordState(record, today)
    if (state === null) continue
    scored.push({
      record,
      state,
      validUntil: record.validUntil,
      issuedOn: record.referralIssuedOn,
      dueOn: referralDueOn(record.referralIssuedOn, record.referralDeadlineDays),
      pending: isPendingMedical(record),
    })
  }
  return summarizeLine(scored) ?? { state: emptyState(context), governing: null, pending: null }
}

/**
 * Szkolenia BHP osoby: każda linia (jednorazowe, okresowe, ppoż.) liczy się
 * osobno, a całość ma stan najgorszej — instruktaż stanowiskowy nie maskuje
 * wygasłego szkolenia okresowego.
 */
export function summarizeBhp(
  records: readonly BhpRecord[],
  today: IsoDay,
  context: HealthPersonContext,
): AreaSummary<BhpRecord> {
  const byLine = new Map<BhpLine, Scored<BhpRecord>[]>()
  for (const record of records) {
    const state = bhpRecordState(record, today)
    if (state === null) continue
    const line = bhpLineOf(record.kind)
    byLine.set(line, [
      ...(byLine.get(line) ?? []),
      {
        record,
        state,
        validUntil: record.validUntil,
        issuedOn: record.referralIssuedOn,
        dueOn: referralDueOn(record.referralIssuedOn, record.referralDeadlineDays),
        pending: record.status === 'planned',
      },
    ])
  }
  const lines = [...byLine.values()]
    .map((rows) => summarizeLine(rows))
    .filter((line): line is AreaSummary<BhpRecord> => line !== null)
  if (lines.length === 0) return { state: emptyState(context), governing: null, pending: null }
  const worst = lines.toSorted((left, right) => SEVERITY[right.state] - SEVERITY[left.state])[0]!
  /* Skierowanie w toku pokazujemy z dowolnej linii — kadry mają je widzieć. */
  const pending = worst.pending ?? lines.find((line) => line.pending !== null)?.pending ?? null
  return { state: worst.state, governing: worst.governing, pending }
}

/** Czy stan wymaga uwagi kadr (licznik „wygasa lub brak" na pulpicie i w asystencie). */
export function healthNeedsAttention(state: HealthState): boolean {
  return state === 'err' || state === 'warn' || state === 'missing'
}
